import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactElement } from 'react';
import { useSession } from './auth/SessionProvider.js';
import { AppLayout } from './components/AppLayout.js';
import { Alert, Spinner } from './components/ui.js';
import type { Permission } from '../shared/permissions.js';

import { SignInPage } from './pages/SignInPage.js';
import { AcceptInvitationPage } from './pages/AcceptInvitationPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { WorkOrderListPage } from './pages/WorkOrderListPage.js';
import { WorkOrderCreatePage } from './pages/WorkOrderCreatePage.js';
import { WorkOrderDetailPage } from './pages/WorkOrderDetailPage.js';
import { ApprovalQueuePage } from './pages/ApprovalQueuePage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { DetailedReportPage } from './pages/DetailedReportPage.js';
import { AuditLogPage } from './pages/AuditLogPage.js';
import { AdminPage } from './pages/admin/AdminPage.js';
import { AdminUsersTab } from './pages/admin/AdminUsersTab.js';
import { AdminTaskOrdersTab } from './pages/admin/AdminTaskOrdersTab.js';
import { AdminCategoriesTab } from './pages/admin/AdminCategoriesTab.js';
import { AdminApprovalAuthorityTab } from './pages/admin/AdminApprovalAuthorityTab.js';
import { AdminRemindersTab } from './pages/admin/AdminRemindersTab.js';
import { ChangePasswordPage } from './pages/ChangePasswordPage.js';
import { HelpPage } from './pages/HelpPage.js';
import { StepGuidePage } from './pages/StepGuidePage.js';
import { ReferenceGuidePage } from './pages/ReferenceGuidePage.js';
import { WorkflowDocPage } from './pages/WorkflowDocPage.js';
import { EmployeeDocsListPage } from './pages/employeeDocs/EmployeeDocsListPage.js';
import { EmployeeDocsPackagePage } from './pages/employeeDocs/EmployeeDocsPackagePage.js';
import { EmployeeDocsTemplatesPage } from './pages/employeeDocs/EmployeeDocsTemplatesPage.js';
import { MyDocumentsPage } from './pages/employeeDocs/MyDocumentsPage.js';
import { UspsDocumentsPage } from './pages/employeeDocs/UspsDocumentsPage.js';
import {
  UspsReleasedDocsListPage,
  UspsReleasedDocsPackagePage,
} from './pages/employeeDocs/UspsReleasedDocsPage.js';

function RequireAuth({ children }: { children: ReactElement }) {
  const { user, loading } = useSession();
  const location = useLocation();

  if (loading) return <Spinner label="Checking your session" />;
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  // A user flagged for a credential change cannot reach the rest of the app first.
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactElement;
}) {
  const { can } = useSession();
  if (!can(permission)) {
    return (
      <Alert variant="error" title="Not authorized">
        You do not have permission to view this page. Contact your administrator if you believe this
        is a mistake.
      </Alert>
    );
  }
  return children;
}

/**
 * Employee documents is one section with two audiences: VGS reviewers/managers see the
 * review workflow; a USPS Contracting Officer sees only the packages released to USPS.
 */
function EmployeeDocsSection({ vgs, usps }: { vgs: ReactElement; usps: ReactElement }) {
  const { can } = useSession();
  if (can('employee_docs.review')) return vgs;
  return <RequirePermission permission="employee_docs.view_released">{usps}</RequirePermission>;
}

export function AppRoutes() {
  const { user, loading } = useSession();

  return (
    <Routes>
      <Route
        path="/sign-in"
        element={loading ? <Spinner /> : user ? <Navigate to="/" replace /> : <SignInPage />}
      />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Link-based access for employees and USPS recipients; no VGSWOM account involved. */}
      <Route path="/my-documents/:token" element={<MyDocumentsPage />} />
      <Route path="/usps-documents/:token" element={<UspsDocumentsPage />} />

      <Route
        path="/change-password"
        element={
          <RequireAuth>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="work-orders" element={<WorkOrderListPage />} />
        <Route
          path="work-orders/new"
          element={
            <RequirePermission permission="work_order.create">
              <WorkOrderCreatePage />
            </RequirePermission>
          }
        />
        <Route path="work-orders/:id" element={<WorkOrderDetailPage />} />
        <Route
          path="approvals"
          element={
            <RequirePermission permission="proposal.approve">
              <ApprovalQueuePage />
            </RequirePermission>
          }
        />
        <Route
          path="reports"
          element={
            <RequirePermission permission="report.view">
              <ReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="reports/detailed"
          element={
            <RequirePermission permission="report.view">
              <DetailedReportPage />
            </RequirePermission>
          }
        />
        <Route
          path="audit-log"
          element={
            <RequirePermission permission="audit.view">
              <AuditLogPage />
            </RequirePermission>
          }
        />
        <Route
          path="employee-documents"
          element={
            <EmployeeDocsSection
              vgs={<EmployeeDocsListPage />}
              usps={<UspsReleasedDocsListPage />}
            />
          }
        />
        <Route
          path="employee-documents/templates"
          element={
            <RequirePermission permission="employee_docs.manage">
              <EmployeeDocsTemplatesPage />
            </RequirePermission>
          }
        />
        <Route
          path="employee-documents/:id"
          element={
            <EmployeeDocsSection
              vgs={<EmployeeDocsPackagePage />}
              usps={<UspsReleasedDocsPackagePage />}
            />
          }
        />
        <Route path="help" element={<HelpPage />} />
        <Route path="help/step-by-step" element={<StepGuidePage />} />
        <Route path="help/reference" element={<ReferenceGuidePage />} />
        <Route path="help/workflow" element={<WorkflowDocPage />} />

        <Route path="admin" element={<AdminPage />}>
          <Route index element={<Navigate to="users" replace />} />
          <Route
            path="users"
            element={
              <RequirePermission permission="admin.user_manage">
                <AdminUsersTab />
              </RequirePermission>
            }
          />
          {/* Facilities are managed with the task order that covers them. */}
          <Route path="facilities" element={<Navigate to="../task-orders" replace />} />
          <Route
            path="task-orders"
            element={
              <RequirePermission permission="admin.task_order_manage">
                <AdminTaskOrdersTab />
              </RequirePermission>
            }
          />
          <Route
            path="categories"
            element={
              <RequirePermission permission="admin.category_manage">
                <AdminCategoriesTab />
              </RequirePermission>
            }
          />
          <Route
            path="approval-authority"
            element={
              <RequirePermission permission="admin.markup_configure">
                <AdminApprovalAuthorityTab />
              </RequirePermission>
            }
          />
          <Route
            path="reminders"
            element={
              <RequirePermission permission="admin.workflow_configure">
                <AdminRemindersTab />
              </RequirePermission>
            }
          />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
